import { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Trash, ProhibitInset, ArrowUUpLeft, Upload, Check, X, DownloadSimple, Eye, CaretLeft, CaretRight, CheckSquare, Square } from '@phosphor-icons/react';
import { getIngestUrl, authFetch } from '../config';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';

type ProcessType = 'loeschung' | 'sperrliste' | 'widerruf';

function QuerulantImport() {
  const [monat, setMonat] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleImport = async () => {
    setImporting(true);
    setResult(null);
    try {
      const res = await authFetch(getIngestUrl('/datenpflege/import-querulanten'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monat: monat || null }),
      });
      const data = await res.json();
      if (data.success) {
        const skipped = data.skipped || 0;
        const parts = [`${data.count} Querulanten importiert`];
        if (skipped > 0) parts.push(`${skipped} Duplikate übersprungen`);
        setResult({ success: true, message: parts.join(', ') });
      } else {
        setResult({ success: false, message: data.error || 'Fehler' });
      }
    } catch (err) {
      setResult({ success: false, message: (err as Error).message });
    }
    setImporting(false);
  };

  return (
    <div style={{
      marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fef2f2',
      borderRadius: '6px', border: '1px solid #fecaca',
    }}>
      <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#dc2626' }}>
        Querulanten aus Dialfire importieren
      </h4>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Ab Monat (optional)</label>
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }} />
        </div>
        <button onClick={handleImport} disabled={importing}
          style={{
            padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none',
            borderRadius: '6px', cursor: importing ? 'wait' : 'pointer', fontSize: '0.85rem',
            opacity: importing ? 0.6 : 1,
          }}>
          {importing ? 'Importiere...' : 'Querulanten sperren'}
        </button>
      </div>
      {result && (
        <div style={{
          marginTop: '0.75rem', padding: '8px 12px', borderRadius: '4px', fontSize: '0.85rem',
          background: result.success ? '#d1fae5' : '#fee2e2',
          color: result.success ? '#065f46' : '#991b1b',
        }}>
          {result.message}
        </div>
      )}
      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
        Importiert alle Kontakte mit fc_status_detail "Querulant" aus dem OptIn Cache als Sperrlisten-Eintraege (inkl. inf3).
      </p>
    </div>
  );
}
type Step = 'upload' | 'mapping' | 'preview' | 'processing' | 'done';

interface ColumnInfo {
  name: string;
  sample_values: string[];
  suggested_mapping?: string;
}

interface ProcessConfig {
  key: ProcessType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  targetSystems: string[];
}

const processConfigs: ProcessConfig[] = [
  {
    key: 'loeschung',
    label: 'Löschung / Anonymisierung',
    description: 'Datenlöschung / Anonymisierung auf Kundenwunsch',
    icon: <Trash size={20} weight="fill" />,
    color: '#dc2626',
    targetSystems: ['Werbepool', 'MuB-Bestand', 'OptIn Cache', 'OptIn Bestand', 'OptIn Status', 'Dialfire', 'BCV', 'Online-Shop']
  },
  {
    key: 'sperrliste',
    label: 'Sperrliste',
    description: 'Querulanten, Blacklist-Einträge aus Dialfire oder Kundenwunsch',
    icon: <ProhibitInset size={20} weight="fill" />,
    color: '#ea580c',
    targetSystems: ['Blacklist', 'Dialfire', 'BCV', 'Online-Shop']
  },
  {
    key: 'widerruf',
    label: 'Widerruf',
    description: 'Widerrufe von Adresslieferanten (LeadOne, Trafego, Red-Lemon)',
    icon: <ArrowUUpLeft size={20} weight="fill" />,
    color: '#7c3aed',
    targetSystems: ['Werbepool', 'Optin-Bestand', 'Dialfire', 'BCV']
  }
];

const coreFields = [
  { value: '', label: 'Nicht zuordnen' },
  { value: 'anrede', label: 'Anrede' },
  { value: 'vorname', label: 'Vorname' },
  { value: 'nachname', label: 'Nachname' },
  { value: 'strasse', label: 'Straße' },
  { value: 'haus_nr', label: 'Hausnummer' },
  { value: 'plz', label: 'PLZ' },
  { value: 'ort', label: 'Ort' },
  { value: 'laenderkennzeichen', label: 'Länderkennzeichen' },
  { value: 'email', label: 'E-Mail' },
  { value: 'telefon', label: 'Telefon' },
  { value: 'geburtsdatum', label: 'Geburtsdatum' },
  { value: 'wkz', label: 'WKZ' },
  { value: 'referenz', label: 'Referenznummer' },
  { value: 'eingangsdatum', label: 'Eingangsdatum' },
  { value: 'herkunft', label: 'Herkunft/Quelle' },
  { value: 'gewinnspiel', label: 'Gewinnspiel' },
];

export default function Datenpflege() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedProcess, setSelectedProcess] = useState<ProcessType | null>(null);
  const [step, setStep] = useState<Step>('upload');
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [records, setRecords] = useState<any[]>([]);
  const [stats, setStats] = useState<{ total: number; valid: number; invalid: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [recentImports, setRecentImports] = useState<any[]>([]);
  const [savedMappingName, setSavedMappingName] = useState<string | null>(null);
  const [originalSavedMapping, setOriginalSavedMapping] = useState<Record<string, string> | null>(null);
  const [headerHash, setHeaderHash] = useState<string | null>(null);
  const [saveMappingDialogOpen, setSaveMappingDialogOpen] = useState(false);
  const [newMappingName, setNewMappingName] = useState('');

  // Detailansicht State
  const [detailView, setDetailView] = useState<{
    importId: number;
    importInfo: any;
    records: any[];
    stats: any;
    pagination: { page: number; limit: number; total: number; pages: number };
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Export-Datumsfilter (vorbelegt mit heute)
  const today = new Date().toISOString().slice(0, 10);
  const [exportDateFrom, setExportDateFrom] = useState(today);
  const [exportDateTo, setExportDateTo] = useState(today);

  // Sammelfunktion State
  const [selectedImports, setSelectedImports] = useState<Set<number>>(new Set());
  const [bulkExportLoading, setBulkExportLoading] = useState(false);

  // Gesamtansicht pro Typ
  const [allDataView, setAllDataView] = useState<{
    processType: ProcessType;
    records: any[];
    stats: any;
    pagination: { page: number; limit: number; total: number; pages: number };
  } | null>(null);
  const [allDataLoading, setAllDataLoading] = useState(false);

  const API_BASE = getIngestUrl('/datenpflege');

  useEffect(() => {
    loadRecentImports();
  }, []);

  const loadRecentImports = async () => {
    try {
      const response = await authFetch(`${API_BASE}/recent`);
      if (response.ok) {
        const data = await response.json();
        setRecentImports(data.imports || []);
      }
    } catch (err) {
      console.error('Failed to load recent imports:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProcess) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_name', file.name);
    formData.append('process_type', selectedProcess);

    try {
      const response = await authFetch(`${API_BASE}/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSourceId(data.data.source_id);
        setColumns(data.data.columns);
        setHeaderHash(data.data.header_hash || null);
        setSavedMappingName(data.data.saved_mapping_name || null);

        // Set initial mapping from suggestions (may include saved mapping)
        const initialMapping: Record<string, string> = {};
        data.data.columns.forEach((col: ColumnInfo) => {
          if (col.suggested_mapping) {
            initialMapping[col.name] = col.suggested_mapping;
          }
        });
        setMapping(initialMapping);
        if (data.data.saved_mapping_name) {
          setOriginalSavedMapping({ ...initialMapping });
        } else {
          setOriginalSavedMapping(null);
        }
        setStep('mapping');
      } else {
        alert('Upload fehlgeschlagen: ' + data.error);
      }
    } catch (err) {
      alert('Fehler beim Upload: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (columnName: string, targetField: string) => {
    const newMapping = { ...mapping };
    if (targetField) {
      newMapping[columnName] = targetField;
    } else {
      delete newMapping[columnName];
    }
    setMapping(newMapping);
  };

  const handleSaveMapping = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/${sourceId}/mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping })
      });

      const data = await response.json();
      if (data.success) {
        setRecords(data.data.records || []);
        setStats(data.data.stats);
        setStep('preview');
      }
    } catch (err) {
      alert('Fehler beim Mapping: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const isMappingChanged = (): boolean => {
    if (!originalSavedMapping) return false;
    const origKeys = Object.keys(originalSavedMapping).filter(k => originalSavedMapping[k]);
    const currKeys = Object.keys(mapping).filter(k => mapping[k]);
    if (origKeys.length !== currKeys.length) return true;
    return origKeys.some(k => originalSavedMapping[k] !== mapping[k]);
  };

  const handleUpdateSavedMapping = async () => {
    if (!savedMappingName || !headerHash) return;

    try {
      const response = await authFetch(`${API_BASE}/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: savedMappingName,
          header_columns: columns.map(c => c.name),
          mapping,
          process_type: selectedProcess
        })
      });

      const data = await response.json();
      if (data.success) {
        setOriginalSavedMapping({ ...mapping });
        alert('Mapping "' + savedMappingName + '" aktualisiert!');
      }
    } catch (err) {
      alert('Fehler beim Aktualisieren: ' + (err as Error).message);
    }
  };

  const handleSaveMappingForReuse = async () => {
    if (!newMappingName.trim()) {
      alert('Bitte einen Namen eingeben');
      return;
    }
    if (!headerHash) {
      alert('Header-Hash fehlt - bitte Datei neu hochladen');
      console.error('headerHash is missing, columns:', columns);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newMappingName.trim(),
          header_columns: columns.map(c => c.name),
          mapping,
          process_type: selectedProcess
        })
      });

      const data = await response.json();
      if (data.success) {
        setSavedMappingName(newMappingName.trim());
        setOriginalSavedMapping({ ...mapping });
        setSaveMappingDialogOpen(false);
        setNewMappingName('');
        alert('Mapping gespeichert! Wird beim naechsten Upload mit gleichen Spalten automatisch angewendet.');
      }
    } catch (err) {
      alert('Fehler beim Speichern: ' + (err as Error).message);
    }
  };

  const handleProcess = async () => {
    if (!stats?.valid) return;

    const config = processConfigs.find(p => p.key === selectedProcess);
    const confirmMsg = selectedProcess === 'loeschung'
      ? `${stats.valid} Datensätze löschen/anonymisieren?\n\nZielsysteme: ${config?.targetSystems.join(', ')}`
      : selectedProcess === 'sperrliste'
      ? `${stats.valid} Datensätze auf die Sperrliste setzen?\n\nZielsysteme: ${config?.targetSystems.join(', ')}`
      : `${stats.valid} Widerrufe verarbeiten?\n\nZielsysteme: ${config?.targetSystems.join(', ')}`;

    if (!window.confirm(confirmMsg)) return;

    setStep('processing');
    setLoading(true);

    try {
      const response = await authFetch(`${API_BASE}/${sourceId}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setStep('done');
        loadRecentImports();
      } else {
        alert('Verarbeitung fehlgeschlagen: ' + (data.message || data.error || 'Unbekannter Fehler'));
        setStep('preview');
      }
    } catch (err) {
      alert('Fehler bei der Verarbeitung: ' + (err as Error).message);
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedProcess(null);
    setStep('upload');
    setSourceId(null);
    setColumns([]);
    setMapping({});
    setRecords([]);
    setStats(null);
  };

  const handleDownload = async (importId: number, target: string = 'all') => {
    try {
      const response = await authFetch(`${API_BASE}/${importId}/export?format=csv&target=${target}`);
      if (!response.ok) throw new Error('Download fehlgeschlagen');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `datenpflege_${target}_${importId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Download fehlgeschlagen: ' + (err as Error).message);
    }
  };

  // Detailansicht laden
  const loadDetails = async (importId: number, page: number = 1) => {
    setDetailLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/${importId}/details?page=${page}&limit=50`);
      if (!response.ok) throw new Error('Details laden fehlgeschlagen');

      const data = await response.json();
      setDetailView({
        importId,
        importInfo: data.import,
        records: data.records,
        stats: data.stats,
        pagination: data.pagination
      });
    } catch (err) {
      alert('Details laden fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  // Sammel-Export
  const handleBulkExport = async (target: string = 'all') => {
    if (selectedImports.size === 0) {
      alert('Bitte mindestens einen Import auswählen');
      return;
    }

    setBulkExportLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/bulk-export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          importIds: Array.from(selectedImports),
          target,
          date_from: exportDateFrom || undefined,
          date_to: exportDateTo || undefined
        })
      });

      if (!response.ok) throw new Error('Export fehlgeschlagen');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `datenpflege_${target}_sammel_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Export fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setBulkExportLoading(false);
    }
  };

  // Import-Auswahl togglen
  const toggleImportSelection = (importId: number) => {
    const newSelection = new Set(selectedImports);
    if (newSelection.has(importId)) {
      newSelection.delete(importId);
    } else {
      newSelection.add(importId);
    }
    setSelectedImports(newSelection);
  };

  // Alle auswählen/abwählen
  const toggleSelectAll = () => {
    if (selectedImports.size === recentImports.length) {
      setSelectedImports(new Set());
    } else {
      setSelectedImports(new Set(recentImports.map(imp => imp.id)));
    }
  };

  // Alle Daten eines Typs laden
  const loadAllData = async (processType: ProcessType, page: number = 1) => {
    setAllDataLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '100' });
      if (exportDateFrom) params.append('date_from', exportDateFrom);
      if (exportDateTo) params.append('date_to', exportDateTo);
      const response = await authFetch(`${API_BASE}/all/${processType}?${params.toString()}`);
      if (!response.ok) throw new Error('Laden fehlgeschlagen');

      const data = await response.json();
      setAllDataView({
        processType,
        records: data.records,
        stats: data.stats,
        pagination: data.pagination
      });
    } catch (err) {
      alert('Laden fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setAllDataLoading(false);
    }
  };

  // Export aller Daten eines Typs
  const handleExportAllByType = async (processType: ProcessType, target: string = 'all') => {
    try {
      const params = new URLSearchParams({ target: target as string });
      if (exportDateFrom) params.append('date_from', exportDateFrom);
      if (exportDateTo) params.append('date_to', exportDateTo);
      const response = await authFetch(`${API_BASE}/all/${processType}/export?${params.toString()}`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `datenpflege_${processType}_alle_${target}_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Export fehlgeschlagen: ' + (err as Error).message);
    }
  };

  const selectedConfig = processConfigs.find(p => p.key === selectedProcess);

  const steps = [
    { key: 'upload', label: 'Upload' },
    { key: 'mapping', label: 'Zuordnung' },
    { key: 'preview', label: 'Vorschau' },
    { key: 'processing', label: 'Verarbeitung' }
  ];
  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <SubPageHeader title="Datenpflege" />

      <main style={{
        maxWidth: '1200px',
        margin: isMobile ? '1rem auto' : '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem'
      }}>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
          <h1 style={{
            margin: '0 0 0.5rem 0',
            color: '#111827',
            fontSize: isMobile ? '1.5rem' : '1.875rem',
            fontWeight: '600'
          }}>
            Datenpflege
          </h1>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: isMobile ? '0.9rem' : '0.95rem'
          }}>
            Löschungen, Sperrlisten und Widerrufe verarbeiten
          </p>
        </div>

        {/* Process Selection */}
        {!selectedProcess && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {processConfigs.map(config => (
                <button
                  key={config.key}
                  onClick={() => setSelectedProcess(config.key)}
                  style={{
                    padding: isMobile ? '1.25rem' : '1.5rem',
                    backgroundColor: 'white',
                    border: `2px solid ${config.color}20`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = config.color;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = `${config.color}20`;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ color: config.color }}>{config.icon}</div>
                    <h3 style={{
                      margin: 0,
                      color: config.color,
                      fontSize: isMobile ? '1.1rem' : '1.25rem',
                      fontWeight: '600'
                    }}>
                      {config.label}
                    </h3>
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    color: '#6b7280',
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    lineHeight: 1.5
                  }}>
                    {config.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {config.targetSystems.slice(0, 3).map(sys => (
                      <span key={sys} style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: `${config.color}10`,
                        color: config.color,
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500'
                      }}>
                        {sys}
                      </span>
                    ))}
                    {config.targetSystems.length > 3 && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '4px',
                        fontSize: '0.7rem'
                      }}>
                        +{config.targetSystems.length - 3}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Gesamtansicht pro Typ */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              padding: '1.25rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                Alle Daten anzeigen & exportieren
              </h3>
              <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>Zeitraum:</label>
                <input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                  style={{
                    padding: '0.4rem 0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.85rem'
                  }}
                />
                <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>bis</span>
                <input
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                  style={{
                    padding: '0.4rem 0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.85rem'
                  }}
                />
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '0.75rem'
              }}>
                {processConfigs.map(config => (
                  <div key={config.key} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    padding: '1rem',
                    backgroundColor: `${config.color}08`,
                    borderRadius: '6px',
                    border: `1px solid ${config.color}20`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ color: config.color }}>{config.icon}</span>
                      <span style={{ fontWeight: '600', color: config.color, fontSize: '0.9rem' }}>{config.label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => loadAllData(config.key)}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: 'white',
                          color: config.color,
                          border: `1px solid ${config.color}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <Eye size={14} /> Alle anzeigen
                      </button>
                      <button
                        onClick={() => handleExportAllByType(config.key, 'all')}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: config.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <DownloadSimple size={14} /> Export
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Imports */}
            {recentImports.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '1rem 1.25rem',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem'
                }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                    Letzte Importe
                  </h3>
                  {/* Sammel-Export Buttons */}
                  {selectedImports.size > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.8rem', color: '#6b7280', alignSelf: 'center' }}>
                        {selectedImports.size} ausgewählt:
                      </span>
                      <button
                        onClick={() => handleBulkExport('all')}
                        disabled={bulkExportLoading}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: '#4f46e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: bulkExportLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <DownloadSimple size={14} />
                        Sammel-Export
                      </button>
                      <button
                        onClick={() => handleBulkExport('dialfire')}
                        disabled={bulkExportLoading}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: bulkExportLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        Dialfire
                      </button>
                      <button
                        onClick={() => handleBulkExport('bcv')}
                        disabled={bulkExportLoading}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: bulkExportLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        BCV
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', width: '40px' }}>
                          <button
                            onClick={toggleSelectAll}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title={selectedImports.size === recentImports.length ? 'Alle abwählen' : 'Alle auswählen'}
                          >
                            {selectedImports.size === recentImports.length && recentImports.length > 0 ? (
                              <CheckSquare size={18} weight="fill" color="#4f46e5" />
                            ) : (
                              <Square size={18} color="#9ca3af" />
                            )}
                          </button>
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Typ</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Datei</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Datum</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Verarbeitet</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentImports.map((imp, idx) => {
                        const config = processConfigs.find(p => p.key === imp.process_type);
                        const isSelected = selectedImports.has(imp.id);
                        return (
                          <tr key={idx} style={{ borderTop: '1px solid #e5e7eb', backgroundColor: isSelected ? '#f0f4ff' : 'transparent' }}>
                            <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                              <button
                                onClick={() => toggleImportSelection(imp.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {isSelected ? (
                                  <CheckSquare size={18} weight="fill" color="#4f46e5" />
                                ) : (
                                  <Square size={18} color="#9ca3af" />
                                )}
                              </button>
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: `${config?.color || '#6b7280'}15`,
                                color: config?.color || '#6b7280',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}>
                                {config?.label || imp.process_type}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151' }}>
                              {imp.source_name}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                              {formatDateTime(imp.created_at)}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151', textAlign: 'right', fontWeight: '600' }}>
                              {imp.processed_count}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                <button
                                  onClick={() => loadDetails(imp.id)}
                                  style={{
                                    padding: '0.4rem 0.6rem',
                                    backgroundColor: 'transparent',
                                    color: '#4f46e5',
                                    border: '1px solid #4f46e5',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontSize: '0.75rem'
                                  }}
                                  title="Daten ansehen"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => handleDownload(imp.id, 'all')}
                                  style={{
                                    padding: '0.4rem 0.6rem',
                                    backgroundColor: 'transparent',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontSize: '0.75rem'
                                  }}
                                  title="Excel herunterladen"
                                >
                                  <DownloadSimple size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detailansicht Modal */}
            {detailView && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }} onClick={() => setDetailView(null)}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '1100px',
                  maxHeight: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }} onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem', fontWeight: '600', color: '#111827' }}>
                        {detailView.importInfo.source_name}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                        {processConfigs.find(p => p.key === detailView.importInfo.process_type)?.label} - {formatDate(detailView.importInfo.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => setDetailView(null)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: '#374151'
                      }}
                    >
                      Schließen
                    </button>
                  </div>

                  {/* Stats */}
                  <div style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    gap: '1.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>{detailView.stats.total}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Gesamt</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#16a34a' }}>{detailView.stats.processed}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Verarbeitet</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#4f46e5' }}>{detailView.stats.matched}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Matched</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#dc2626' }}>{detailView.stats.failed}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Fehlgeschlagen</div>
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    {detailLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Lade Daten...</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Status</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Name</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Adresse</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Kontakt</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>WKZ</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailView.records.map((r, idx) => {
                            let matchInfo = '';
                            if (r.match_details) {
                              try {
                                const matches = JSON.parse(r.match_details);
                                if (matches && matches.length > 0) {
                                  const m = matches[0];
                                  matchInfo = `${m.vorname || ''} ${m.nachname || ''}, ${m.plz || ''} (ID:${m.wpool_id})`;
                                }
                              } catch (e) {}
                            }
                            return (
                              <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                  {r.status === 'processed' && r.matched_id ? (
                                    <span style={{
                                      padding: '0.2rem 0.5rem',
                                      backgroundColor: '#d1fae5',
                                      color: '#065f46',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      fontWeight: '500'
                                    }}>
                                      <Check size={10} style={{ marginRight: '0.2rem' }} />Match
                                    </span>
                                  ) : r.status === 'processed' ? (
                                    <span style={{
                                      padding: '0.2rem 0.5rem',
                                      backgroundColor: '#fef3c7',
                                      color: '#92400e',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      fontWeight: '500'
                                    }}>
                                      Kein Match
                                    </span>
                                  ) : (
                                    <span style={{
                                      padding: '0.2rem 0.5rem',
                                      backgroundColor: '#fee2e2',
                                      color: '#991b1b',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      fontWeight: '500'
                                    }}>
                                      {r.status}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: '500', color: '#111827' }}>
                                  {r.vorname} {r.nachname}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                  {r.strasse && `${r.strasse}${r.haus_nr ? ' ' + r.haus_nr : ''}, `}{r.laenderkennzeichen ? `${r.laenderkennzeichen}-` : ''}{r.plz} {r.ort}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                  {r.telefon || r.email || '-'}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                  {r.wkz || '-'}
                                </td>
                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6b7280', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {matchInfo || (r.error_message || '-')}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {detailView.pagination.pages > 1 && (
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#f9fafb'
                    }}>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        Seite {detailView.pagination.page} von {detailView.pagination.pages} ({detailView.pagination.total} Einträge)
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => loadDetails(detailView.importId, detailView.pagination.page - 1)}
                          disabled={detailView.pagination.page <= 1 || detailLoading}
                          style={{
                            padding: '0.4rem 0.75rem',
                            backgroundColor: detailView.pagination.page <= 1 ? '#f3f4f6' : 'white',
                            color: detailView.pagination.page <= 1 ? '#9ca3af' : '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: detailView.pagination.page <= 1 ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          <CaretLeft size={14} /> Zurück
                        </button>
                        <button
                          onClick={() => loadDetails(detailView.importId, detailView.pagination.page + 1)}
                          disabled={detailView.pagination.page >= detailView.pagination.pages || detailLoading}
                          style={{
                            padding: '0.4rem 0.75rem',
                            backgroundColor: detailView.pagination.page >= detailView.pagination.pages ? '#f3f4f6' : 'white',
                            color: detailView.pagination.page >= detailView.pagination.pages ? '#9ca3af' : '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: detailView.pagination.page >= detailView.pagination.pages ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          Weiter <CaretRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gesamtansicht Modal (alle Daten eines Typs) */}
            {allDataView && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }} onClick={() => setAllDataView(null)}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '1200px',
                  maxHeight: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }} onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f9fafb',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem', fontWeight: '600', color: processConfigs.find(p => p.key === allDataView.processType)?.color }}>
                        Alle {processConfigs.find(p => p.key === allDataView.processType)?.label}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                        {allDataView.stats.total} Einträge aus {allDataView.stats.imports} Importen
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleExportAllByType(allDataView.processType, 'all')}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: processConfigs.find(p => p.key === allDataView.processType)?.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <DownloadSimple size={16} /> Alle exportieren
                      </button>
                      <button
                        onClick={() => handleExportAllByType(allDataView.processType, 'dialfire')}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        Dialfire
                      </button>
                      <button
                        onClick={() => handleExportAllByType(allDataView.processType, 'bcv')}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        BCV
                      </button>
                      <button
                        onClick={() => setAllDataView(null)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#f3f4f6',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          color: '#374151'
                        }}
                      >
                        Schließen
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    gap: '1.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>{allDataView.stats.total}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Gesamt</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#16a34a' }}>{allDataView.stats.processed}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Verarbeitet</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#4f46e5' }}>{allDataView.stats.matched}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Matched</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#6b7280' }}>{allDataView.stats.imports}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Importe</div>
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    {allDataLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Lade Daten...</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Status</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Name</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Adresse</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Kontakt</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>WKZ</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Quelle</th>
                            <th style={{ padding: '0.65rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Import</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allDataView.records.map((r, idx) => (
                            <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.5rem 0.75rem' }}>
                                {r.status === 'processed' && r.matched_id ? (
                                  <span style={{
                                    padding: '0.2rem 0.5rem',
                                    backgroundColor: '#d1fae5',
                                    color: '#065f46',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500'
                                  }}>
                                    Match
                                  </span>
                                ) : r.status === 'processed' ? (
                                  <span style={{
                                    padding: '0.2rem 0.5rem',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500'
                                  }}>
                                    Kein Match
                                  </span>
                                ) : (
                                  <span style={{
                                    padding: '0.2rem 0.5rem',
                                    backgroundColor: '#e5e7eb',
                                    color: '#6b7280',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500'
                                  }}>
                                    {r.status}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: '500', color: '#111827' }}>
                                {r.vorname} {r.nachname}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                {r.strasse && `${r.strasse}${r.haus_nr ? ' ' + r.haus_nr : ''}, `}{r.laenderkennzeichen ? `${r.laenderkennzeichen}-` : ''}{r.plz} {r.ort}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                {r.telefon || r.email || '-'}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                {r.wkz || '-'}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6b7280', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.source_name || '-'}
                              </td>
                              <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                {formatDate(r.import_date)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {allDataView.pagination.pages > 1 && (
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#f9fafb'
                    }}>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        Seite {allDataView.pagination.page} von {allDataView.pagination.pages} ({allDataView.pagination.total} Einträge)
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => loadAllData(allDataView.processType, allDataView.pagination.page - 1)}
                          disabled={allDataView.pagination.page <= 1 || allDataLoading}
                          style={{
                            padding: '0.4rem 0.75rem',
                            backgroundColor: allDataView.pagination.page <= 1 ? '#f3f4f6' : 'white',
                            color: allDataView.pagination.page <= 1 ? '#9ca3af' : '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: allDataView.pagination.page <= 1 ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          <CaretLeft size={14} /> Zurück
                        </button>
                        <button
                          onClick={() => loadAllData(allDataView.processType, allDataView.pagination.page + 1)}
                          disabled={allDataView.pagination.page >= allDataView.pagination.pages || allDataLoading}
                          style={{
                            padding: '0.4rem 0.75rem',
                            backgroundColor: allDataView.pagination.page >= allDataView.pagination.pages ? '#f3f4f6' : 'white',
                            color: allDataView.pagination.page >= allDataView.pagination.pages ? '#9ca3af' : '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: allDataView.pagination.page >= allDataView.pagination.pages ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          Weiter <CaretRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Process Flow */}
        {selectedProcess && step !== 'done' && (
          <>
            {/* Progress Steps */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              padding: isMobile ? '1rem' : '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <div style={{ color: selectedConfig?.color }}>{selectedConfig?.icon}</div>
                <h2 style={{
                  margin: 0,
                  color: selectedConfig?.color,
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  fontWeight: '600'
                }}>
                  {selectedConfig?.label}
                </h2>
                <button
                  onClick={resetForm}
                  style={{
                    marginLeft: 'auto',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}
                >
                  Abbrechen
                </button>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflowX: 'auto',
                minWidth: isMobile ? '400px' : 'auto'
              }}>
                {steps.map((s, idx) => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: step === s.key ? selectedConfig?.color : currentStepIndex > idx ? '#10b981' : '#e5e7eb',
                      color: step === s.key || currentStepIndex > idx ? 'white' : '#6b7280',
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      flexShrink: 0
                    }}>
                      {currentStepIndex > idx ? <Check size={14} weight="bold" /> : idx + 1}
                    </div>
                    <div style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: step === s.key ? selectedConfig?.color : '#6b7280',
                      whiteSpace: 'nowrap'
                    }}>
                      {s.label}
                    </div>
                    {idx < steps.length - 1 && (
                      <div style={{
                        flex: 1,
                        height: '2px',
                        backgroundColor: currentStepIndex > idx ? '#10b981' : '#e5e7eb',
                        marginLeft: '0.75rem',
                        minWidth: '20px'
                      }} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              padding: isMobile ? '1.5rem' : '2rem'
            }}>
              {/* Upload Step */}
              {step === 'upload' && (
                <>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.1rem' }}>
                    Datei hochladen
                  </h3>
                  <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                    CSV oder Excel-Datei mit den zu verarbeitenden Datensätzen
                  </p>

                  <div style={{
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px',
                    padding: '2rem',
                    textAlign: 'center',
                    backgroundColor: '#f9fafb',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    position: 'relative'
                  }}>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      disabled={loading}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <Upload size={48} color={selectedConfig?.color} style={{ marginBottom: '1rem' }} />
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500', color: '#374151' }}>
                      {loading ? 'Wird hochgeladen...' : 'Datei auswählen oder hierher ziehen'}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                      CSV, XLS oder XLSX
                    </p>
                  </div>

                  {/* Expected Format Info */}
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: `${selectedConfig?.color}08`,
                    borderRadius: '6px',
                    border: `1px solid ${selectedConfig?.color}20`
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: selectedConfig?.color }}>
                      Erwartete Felder
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6 }}>
                      {selectedProcess === 'loeschung' && 'Vorname, Nachname, Straße, PLZ, Ort, ggf. Email, Telefon, Auftragsdatum'}
                      {selectedProcess === 'sperrliste' && 'Anrede, Vorname, Nachname, Straße, PLZ, Ort, Telefon, ggf. Email'}
                      {selectedProcess === 'widerruf' && 'Vorname, Nachname, Straße, PLZ, Ort, Telefon, WKZ, Eingangsdatum, Herkunft'}
                    </p>
                  </div>

                  {/* Querulanten-Import (nur bei Sperrliste) */}
                  {selectedProcess === 'sperrliste' && (
                    <QuerulantImport />
                  )}
                </>
              )}

              {/* Mapping Step */}
              {step === 'mapping' && (
                <>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.1rem' }}>
                    Spalten zuordnen
                  </h3>

                  {savedMappingName && (
                    <div style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: isMappingChanged() ? '#fef3c7' : '#d1fae5',
                      border: `1px solid ${isMappingChanged() ? '#fbbf24' : '#6ee7b7'}`,
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Check size={18} weight="bold" color={isMappingChanged() ? '#d97706' : '#059669'} />
                      <span style={{ color: isMappingChanged() ? '#92400e' : '#065f46', fontSize: '0.9rem' }}>
                        {isMappingChanged()
                          ? <>Mapping "<strong>{savedMappingName}</strong>" wurde geaendert</>
                          : <>Gespeichertes Mapping "<strong>{savedMappingName}</strong>" automatisch angewendet</>
                        }
                      </span>
                    </div>
                  )}

                  <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                    Ordnen Sie die Spalten den entsprechenden Feldern zu
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {columns.map((col) => (
                      <div key={col.name} style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: isMobile ? '0.5rem' : '1rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', color: '#111827', fontSize: '0.9rem' }}>
                            {col.name}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#6b7280',
                            marginTop: '0.25rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {col.sample_values.slice(0, 2).join(', ')}
                          </div>
                        </div>
                        <select
                          value={mapping[col.name] || ''}
                          onChange={(e) => handleMappingChange(col.name, e.target.value)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            fontSize: '0.9rem',
                            minWidth: isMobile ? 'auto' : '180px',
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          {coreFields.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleSaveMapping}
                      disabled={loading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: loading ? '#9ca3af' : selectedConfig?.color,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        flex: isMobile ? '1' : 'none'
                      }}
                    >
                      {loading ? 'Verarbeite...' : 'Weiter zur Vorschau'}
                    </button>

                    {savedMappingName && isMappingChanged() && (
                      <button
                        onClick={handleUpdateSavedMapping}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          flex: isMobile ? '1' : 'none'
                        }}
                      >
                        Mapping "{savedMappingName}" aktualisieren
                      </button>
                    )}

                    {!savedMappingName && (
                      <button
                        onClick={() => setSaveMappingDialogOpen(true)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          flex: isMobile ? '1' : 'none'
                        }}
                      >
                        Mapping speichern
                      </button>
                    )}
                  </div>

                  {/* Save Mapping Dialog */}
                  {saveMappingDialogOpen && (
                    <div style={{
                      position: 'fixed',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}>
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        width: '400px',
                        maxWidth: '90vw'
                      }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#111827' }}>Mapping speichern</h4>
                        <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                          Geben Sie einen Namen fuer dieses Mapping ein. Es wird beim naechsten Upload mit gleichen Spalten automatisch angewendet.
                        </p>
                        <input
                          type="text"
                          value={newMappingName}
                          onChange={(e) => setNewMappingName(e.target.value)}
                          placeholder="z.B. Dialfire Export"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.95rem',
                            marginBottom: '1rem'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setSaveMappingDialogOpen(false)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            Abbrechen
                          </button>
                          <button
                            onClick={handleSaveMappingForReuse}
                            disabled={!newMappingName.trim()}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: newMappingName.trim() ? '#10b981' : '#9ca3af',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: newMappingName.trim() ? 'pointer' : 'not-allowed'
                            }}
                          >
                            Speichern
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Preview Step */}
              {step === 'preview' && (
                <>
                  <h3 style={{ margin: '0 0 1.5rem 0', color: '#374151', fontSize: '1.1rem' }}>
                    Vorschau
                  </h3>

                  {/* Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                        {stats?.total || 0}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Gesamt</div>
                    </div>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      border: '1px solid #bbf7d0'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>
                        {stats?.valid || 0}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Gültig</div>
                    </div>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#fef2f2',
                      borderRadius: '6px',
                      border: '1px solid #fecaca'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc2626' }}>
                        {stats?.invalid || 0}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Ungültig</div>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Status</th>
                          <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Name</th>
                          <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Adresse</th>
                          <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Kontakt</th>
                          <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>Treffer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.slice(0, 10).map((r, idx) => (
                          <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.65rem 0.85rem' }}>
                              {r.valid ? (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: '#d1fae5',
                                  color: '#065f46',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}>
                                  <Check size={12} /> OK
                                </span>
                              ) : (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: '#fee2e2',
                                  color: '#991b1b',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}>
                                  <X size={12} /> {r.error || 'Fehler'}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.875rem' }}>
                              <div style={{ fontWeight: '500', color: '#111827' }}>
                                {r.vorname} {r.nachname}
                              </div>
                            </td>
                            <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.85rem', color: '#6b7280' }}>
                              {r.strasse && `${r.strasse}${r.haus_nr ? ' ' + r.haus_nr : ''}, `}{r.plz} {r.ort}
                            </td>
                            <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.85rem', color: '#6b7280' }}>
                              {r.email || r.telefon || '-'}
                            </td>
                            <td style={{ padding: '0.65rem 0.85rem', fontSize: '0.75rem' }}>
                              {r.match_hits && r.match_hits.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                  {r.match_hits.map((h: string, hi: number) => {
                                    const fuzzy = h.includes('~');
                                    return (
                                    <span key={hi} style={{
                                      padding: '1px 5px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600,
                                      background: fuzzy ? '#fef3c7' : '#dcfce7',
                                      color: fuzzy ? '#92400e' : '#166534',
                                    }}>{h}</span>
                                    );
                                  })}
                                </div>
                              ) : r.valid ? (
                                <span style={{ color: '#dc2626', fontSize: '0.7rem' }}>Kein Treffer</span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {records.length > 10 && (
                      <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>
                        ... und {records.length - 10} weitere Datensätze
                      </div>
                    )}
                  </div>

                  {/* Target Systems Info */}
                  <div style={{
                    padding: '1rem',
                    backgroundColor: `${selectedConfig?.color}08`,
                    borderRadius: '6px',
                    border: `1px solid ${selectedConfig?.color}20`,
                    marginBottom: '1.5rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: selectedConfig?.color }}>
                      Zielsysteme
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {selectedConfig?.targetSystems.map(sys => (
                        <span key={sys} style={{
                          padding: '0.35rem 0.75rem',
                          backgroundColor: 'white',
                          border: `1px solid ${selectedConfig?.color}40`,
                          color: selectedConfig?.color,
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {sys}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '1rem'
                  }}>
                    <button
                      onClick={() => setStep('mapping')}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: 'white',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                      }}
                    >
                      Zurück
                    </button>
                    <button
                      onClick={handleProcess}
                      disabled={loading || !stats?.valid}
                      style={{
                        flex: isMobile ? undefined : 1,
                        padding: '0.75rem 1.5rem',
                        backgroundColor: (loading || !stats?.valid) ? '#9ca3af' : selectedConfig?.color,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (loading || !stats?.valid) ? 'not-allowed' : 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                      }}
                    >
                      {stats?.valid || 0} Datensätze verarbeiten
                    </button>
                  </div>
                </>
              )}

              {/* Processing Step */}
              {step === 'processing' && (
                <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    border: '4px solid #e5e7eb',
                    borderTopColor: selectedConfig?.color,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 1.5rem'
                  }} />
                  <h3 style={{ margin: '0 0 0.5rem 0', color: selectedConfig?.color, fontSize: '1.25rem' }}>
                    Verarbeitung läuft...
                  </h3>
                  <p style={{ margin: 0, color: '#6b7280' }}>
                    Daten werden in die Zielsysteme übertragen
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Done */}
        {step === 'done' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            padding: '3rem 2rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <Check size={32} weight="bold" color="#059669" />
            </div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#059669', fontSize: '1.5rem' }}>
              Verarbeitung abgeschlossen
            </h2>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              {stats?.valid} Datensätze wurden erfolgreich verarbeitet
            </p>

            {/* Download Section */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '0.95rem' }}>
                <DownloadSimple size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Daten für externe Systeme exportieren
              </h4>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '0.75rem',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => sourceId && handleDownload(sourceId, 'all')}
                  style={{
                    padding: '0.6rem 1.25rem',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <DownloadSimple size={16} />
                  Alle Daten (Excel)
                </button>
                <button
                  onClick={() => sourceId && handleDownload(sourceId, 'dialfire')}
                  style={{
                    padding: '0.6rem 1.25rem',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <DownloadSimple size={16} />
                  Dialfire
                </button>
                <button
                  onClick={() => sourceId && handleDownload(sourceId, 'bcv')}
                  style={{
                    padding: '0.6rem 1.25rem',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <DownloadSimple size={16} />
                  BCV
                </button>
              </div>
              <p style={{ margin: '1rem 0 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                Für Online-Shop: "Alle Daten" herunterladen und manuell verarbeiten
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '0.75rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  // Reset nur Formular, aber Prozesstyp behalten
                  setStep('upload');
                  setSourceId(null);
                  setColumns([]);
                  setMapping({});
                  setRecords([]);
                  setStats(null);
                  setSavedMappingName(null);
                  setOriginalSavedMapping(null);
                  setHeaderHash(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: selectedConfig?.color,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                Weitere {selectedConfig?.label}-Datei erfassen
              </button>
              <button
                onClick={resetForm}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                Zurück zum Menü Datenpflege
              </button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
